/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react"
import { getGrabXValue } from "../grabx/getter"
import { TFacets, GrabXDataTypes, GrabXDataTypeMap } from "../grabx/types"
import { apiReducerPaths } from "./constants"

export interface GrabXFeatureFlagParams<
  T extends GrabXDataTypes = GrabXDataTypes
> {
  variableName: string
  dataType: T
  defaultValue: GrabXDataTypeMap[T]
  facets?: TFacets
}

export const grabXApi = createApi({
  reducerPath: apiReducerPaths.grabXApi,
  baseQuery: fakeBaseQuery(),
  keepUnusedDataFor: Infinity,
  endpoints: builder => ({
    getGrabXFeatureFlag: builder.query<any, GrabXFeatureFlagParams>({
      queryFn: async ({ variableName, dataType, defaultValue, facets }) => {
        try {
          return {
            data: await getGrabXValue(
              variableName,
              dataType,
              defaultValue,
              facets,
            ),
          }
        } catch (error) {
          return {
            error: {
              message: error instanceof Error ? error.message : "Unknown error",
            },
          }
        }
      },
    }),
  }),
})

export const { useGetGrabXFeatureFlagQuery } = grabXApi

/**
 * Typed wrapper hook that infers the return type from dataType parameter.
 * Use this instead of useGetGrabXFeatureFlagQuery for proper type inference.
 */
export function useGrabXFeatureFlag<T extends GrabXDataTypes>(
  params: GrabXFeatureFlagParams<T>,
): Omit<
  ReturnType<typeof useGetGrabXFeatureFlagQuery>,
  "data" | "currentData"
> & {
  data: GrabXDataTypeMap[T] | undefined
  /** Result for the CURRENT args only — undefined while a new arg refetches. */
  currentData: GrabXDataTypeMap[T] | undefined
} {
  const result = useGetGrabXFeatureFlagQuery(params as GrabXFeatureFlagParams)
  return {
    ...result,
    data: result.data as GrabXDataTypeMap[T] | undefined,
    currentData: result.currentData as GrabXDataTypeMap[T] | undefined,
  }
}
